import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AsyncPipe, DatePipe } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router'
import { combineLatest, filter, map, shareReplay, switchMap } from 'rxjs'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { FormsModule } from '@angular/forms'
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay'
import { MatChipsModule } from '@angular/material/chips'
import { MatIcon } from '@angular/material/icon'
import { MatButton } from '@angular/material/button'
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field'
import { MatSuffix } from '@angular/material/input'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { endOfDay } from 'date-fns'
import { ActivityLogEntry, ActivityLogSettingsChange, MetricsService } from '../metrics.service'
import { MetricType } from '../calls.service'
import { User, UsersService } from '../users.service'
import { AppTableColumn, AppTableSource, TableComponent } from '../table/table.component'
import { readDateRangeFromQueryParams } from '../common/date-range-picker/date-range-picker.component'
import { BritishDatePipe } from '../british-date.pipe'
import { DATE_TIME_FORMAT } from '../utils'
import { SpinnerComponent } from '../spinner/spinner.component'
import { FilterComponent } from '../filters/filter/filter.component'
import { MultiFilterComponent } from '../multi-filter/multi-filter.component'
import { constructNameForUser } from '../utils'
import { emitPaginationQueryParameterChanges, getPaginationOptionsFromQueryParamMap } from '../list-query-options'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { ClientOrganization, ClientsService } from '../clients.service'

type ActionOption = { id: MetricType; label: string }

// The MetricType enum is mirrored by hand on the frontend and grows over time, so an action without a
// translation key must still render readably rather than leaking the raw key to an admin.
function humanizeToken(token: string): string {
  return token
    .split('_')
    .map((word, index) => (index === 0 ? word.charAt(0) + word.slice(1).toLowerCase() : word.toLowerCase()))
    .join(' ')
}

function humanizeFieldName(field: string): string {
  const spaced = field.replace(/([A-Z])/g, ' $1')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

@Component({
  selector: 'app-activity-log',
  standalone: true,
  imports: [
    AsyncPipe,
    DatePipe,
    TranslatePipe,
    TranslateDirective,
    FormsModule,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MatChipsModule,
    MatIcon,
    MatButton,
    MatFormField,
    MatLabel,
    MatHint,
    MatSuffix,
    MatDatepickerModule,
    TableComponent,
    SpinnerComponent,
    FilterComponent,
    MultiFilterComponent,
    ToolbarComponent,
  ],
  providers: [BritishDatePipe],
  templateUrl: './activity-log.component.html',
  styleUrl: './activity-log.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ActivityLogComponent {
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private metricsService = inject(MetricsService)
  private usersService = inject(UsersService)
  private translateService = inject(TranslateService)
  private britishDatePipe = inject(BritishDatePipe)

  private clientsService = inject(ClientsService)

  clientOrganizations$ = this.clientsService.getCurrentUserClient().pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data?.organizations ?? []),
    shareReplay(1)
  )

  clientUsers$ = this.usersService.getClientUsers()

  private selectedOrganizationIds$ = combineLatest([this.clientOrganizations$, this.route.queryParamMap]).pipe(
    map(([organizations, queryParamMap]) => {
      const selectedIds = queryParamMap
        .getAll('organizationIds')
        .map((organizationId) => parseInt(organizationId))
        .filter((organizationId) => organizations.some((organization) => organization.id === organizationId))
      return { organizations, selectedIds }
    }),
    shareReplay(1)
  )

  // The date range picker writes both dates in one navigation (see commitDateRange). Binding the inputs
  // to these local fields — rather than straight to the query params — keeps a mid-selection start date
  // from being clobbered while the user is still choosing the end date.
  selectedStartDate: Date | null = null
  selectedEndDate: Date | null = null

  isDateRangeOverlayOpen = false

  get hasDateRange(): boolean {
    return this.selectedStartDate !== null || this.selectedEndDate !== null
  }

  private syncSelectedDates = this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((queryParamMap) => {
    const startDateParam = queryParamMap.get('startDate')
    const endDateParam = queryParamMap.get('endDate')
    this.selectedStartDate = startDateParam !== null ? new Date(startDateParam) : null
    this.selectedEndDate = endDateParam !== null ? new Date(endDateParam) : null
  })

  actionOptions$ = this.translateService.stream('ACTIVITY_LOG.ACTIONS').pipe(
    map((actions: { [metric: string]: string }) => {
      if (actions === null || typeof actions !== 'object') {
        return []
      }
      return Object.entries(actions)
        .map(([metric, label]) => ({ id: metric as MetricType, label }))
        .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label))
    })
  )

  filters$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => {
      const userIdParam = queryParamMap.get('userId')
      return {
        userId: userIdParam !== null ? parseInt(userIdParam) : null,
        metrics: queryParamMap.getAll('metrics') as MetricType[],
      }
    })
  )

  /**
   * The page keeps the filters; the table keeps the paging and sorting. Rebinding this whole object
   * is what tells the table a filter moved, so it is rebuilt rather than mutated.
   */
  source$ = combineLatest([this.route.queryParamMap, this.selectedOrganizationIds$]).pipe(
    map(([queryParamMap, { organizations, selectedIds }]): AppTableSource<ActivityLogEntry> => {
      const userIdParam = queryParamMap.get('userId')
      // Read through the shared parser, which drops what it cannot parse: these come from the address
      // bar, and a hand-edited one would otherwise reach toISOString and throw.
      const { startDate, endDate } = readDateRangeFromQueryParams(queryParamMap)
      const metrics = queryParamMap.getAll('metrics')
      return {
        url: '/api/metrics/activity-log',
        params: {
          // An empty selection means the whole client, which is what this log shows by default.
          organizationIds: (selectedIds.length > 0 ? selectedIds : organizations.map((organization) => organization.id)).map(String),
          ...(userIdParam !== null ? { userId: userIdParam } : {}),
          ...(metrics.length > 0 ? { metrics } : {}),
          ...(startDate !== null ? { startDate: startDate.toISOString() } : {}),
          // The picker yields midnight, but an admin selecting a day expects that whole day included.
          ...(endDate !== null ? { endDate: endOfDay(endDate).toISOString() } : {}),
        },
      }
    })
  )

  view$ = combineLatest([this.source$, this.filters$, this.clientUsers$, this.actionOptions$, this.selectedOrganizationIds$]).pipe(
    map(([source, filters, clientUsers, actionOptions, { organizations, selectedIds }]) => ({
      source,
      filters,
      users: clientUsers.data ?? [],
      actionOptions,
      organizations,
      selectedOrganizationIds: selectedIds,
      // One organization means the column only ever says the same thing.
      columns: organizations.length > 1 ? this.columns : this.columns.filter((column) => column.id !== 'organization'),
    }))
  )

  readonly columns: AppTableColumn<ActivityLogEntry>[] = [
    {
      id: 'timestamp',
      header: 'COMMON.DATE',
      value: (entry) => new Date(entry.timestamp).getTime(),
      display: (entry) => this.britishDatePipe.transform(entry.timestamp, DATE_TIME_FORMAT),
      sortable: true,
    },
    { id: 'user', header: 'COMMON.USER', value: (entry) => this.actorName(entry), tone: 'strong' },
    { id: 'organization', header: 'COMMON.ORGANIZATION', value: (entry) => entry.organization?.name ?? '' },
    { id: 'metric', header: 'ACTIVITY_LOG.ACTION', value: (entry) => this.translateAction(entry.metric), sortable: true },
    { id: 'details', header: 'ACTIVITY_LOG.DETAILS', value: (entry) => this.describeDetails(entry), tone: 'muted' },
    { id: 'call', header: 'COMMON.CALL', value: (entry) => entry.call?.name ?? '' },
  ]

  rowId = (entry: ActivityLogEntry): string => String(entry.id)

  translateAction(metric: MetricType): string {
    const translationKey = `ACTIVITY_LOG.ACTIONS.${metric}`
    const translated = this.translateService.instant(translationKey)
    return translated === translationKey ? humanizeToken(metric) : translated
  }

  actorName(entry: ActivityLogEntry): string {
    if (entry.performedByBriefAiSupport) {
      return this.translateService.instant('ACTIVITY_LOG.BRIEF_AI_SUPPORT')
    }
    return entry.user?.name ?? ''
  }

  describeDetails(entry: ActivityLogEntry): string {
    if (entry.settingsChanges !== null) {
      return entry.settingsChanges.map((change) => `${this.fieldLabel(change.field)}: ${this.formatSettingValue(change.value)}`).join('; ')
    }
    if (entry.affectedUserName !== null) {
      return entry.affectedUserName
    }
    return ''
  }

  private fieldLabel(field: string): string {
    const translationKey = `ACTIVITY_LOG.FIELDS.${field}`
    const translated = this.translateService.instant(translationKey)
    return translated === translationKey ? humanizeFieldName(field) : translated
  }

  private formatSettingValue(value: unknown): string {
    if (typeof value === 'boolean') {
      return this.translateService.instant(value ? 'ACTIVITY_LOG.VALUE_ON' : 'ACTIVITY_LOG.VALUE_OFF')
    }
    if (value === null || value === undefined || value === '') {
      return this.translateService.instant('ACTIVITY_LOG.VALUE_NONE')
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.map((item) => this.formatSettingValue(item)).join(', ') : this.translateService.instant('ACTIVITY_LOG.VALUE_NONE')
    }
    if (typeof value === 'string' && /^[A-Z][A-Z0-9_]*$/.test(value)) {
      return humanizeToken(value)
    }
    return String(value)
  }

  displayUser(user: User | null) {
    return user !== null ? constructNameForUser(user) : ''
  }

  findUser(users: User[], id: number) {
    return users.find((user) => user.id === id) ?? null
  }

  filterUsers(query: string, users: User[]) {
    const lowerCaseQuery = query.toLowerCase()
    return users.filter((user) => constructNameForUser(user).toLowerCase().includes(lowerCaseQuery) || user.email.toLowerCase().includes(lowerCaseQuery))
  }

  displayAction(option: ActionOption) {
    return option.label
  }

  displaySelectedActions = (metrics: MetricType[]) => {
    if (metrics.length <= 2) {
      return metrics.map((metric) => this.translateAction(metric)).join(', ')
    }
    return this.translateService.instant('ACTIVITY_LOG.ACTIONS_SELECTED', { count: metrics.length })
  }

  updateUserId(userId: number | null) {
    this.updateQueryParams({ userId })
  }

  updateMetrics(metrics: MetricType[]) {
    this.updateQueryParams({ metrics: metrics.length > 0 ? metrics : null })
  }

  commitDateRange() {
    this.updateQueryParams({
      startDate: this.selectedStartDate !== null ? this.selectedStartDate.toISOString() : null,
      endDate: this.selectedEndDate !== null ? this.selectedEndDate.toISOString() : null,
    })
  }

  applyDateRange() {
    this.commitDateRange()
    this.isDateRangeOverlayOpen = false
  }

  clearDateRange() {
    this.selectedStartDate = null
    this.selectedEndDate = null
    this.commitDateRange()
  }

  updateOrganizationIds(organizationIds: number[]) {
    this.updateQueryParams({ organizationIds: organizationIds.length > 0 ? organizationIds.map(String) : null })
  }

  displayOrganization(organization: ClientOrganization): string {
    return organization.name
  }

  displaySelectedOrganizations(organizations: ClientOrganization[]) {
    return (ids: number[]) =>
      organizations
        .filter((organization) => ids.includes(organization.id))
        .map((organization) => organization.name)
        .join(', ')
  }

  private updateQueryParams(queryParams: { [key: string]: string | number | string[] | null }) {
    this.router.navigate([], { queryParams: { ...queryParams, page: 0 }, queryParamsHandling: 'merge', relativeTo: this.route })
  }
}
